import { useEffect, useState } from "react";
import { CalendarClock, Link2, Unlink, CheckCircle2, AlertCircle, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminCalendarPage() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/oauth/calendar/status");
            setStatus(r.data);
        } catch (e) {
            setStatus({ configured: false, connected: false });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const connect = () => {
        window.location.href = `${BACKEND}/api/oauth/calendar/login`;
    };

    const disconnect = async () => {
        if (!confirm("Наистина ли искате да прекъснете връзката с Google Calendar? Съществуващите резервации ще останат в календара, но новите няма да се добавят автоматично.")) return;
        await api.post("/oauth/calendar/disconnect");
        load();
    };

    if (loading) {
        return <div className="text-ink-muted">Зареждане…</div>;
    }

    const connected = status?.connected;
    const configured = status?.configured;

    return (
        <div className="space-y-8 max-w-3xl" data-testid="admin-calendar-page">
            <div>
                <h1 className="font-display text-3xl mb-2">Google Calendar</h1>
                <p className="text-ink-muted text-sm">
                    Свържете Google Calendar, за да се създават събития в реално време при всяка нова резервация.
                </p>
            </div>

            {!configured && (
                <div className="p-5 rounded-2xl bg-surface border border-line flex gap-3">
                    <AlertCircle className="text-gold shrink-0" size={20} />
                    <div className="text-sm text-ink-muted">
                        Google OAuth не е конфигуриран на сървъра. Свържете се с администратора за да добави
                        <code className="mx-1 px-1.5 py-0.5 bg-bg rounded text-xs">GOOGLE_CLIENT_ID</code> и
                        <code className="mx-1 px-1.5 py-0.5 bg-bg rounded text-xs">GOOGLE_CLIENT_SECRET</code>.
                    </div>
                </div>
            )}

            {configured && !connected && (
                <div className="p-8 rounded-3xl bg-surface border border-line">
                    <div className="w-14 h-14 rounded-full bg-gold/10 border border-gold/20 text-gold flex items-center justify-center mb-4">
                        <CalendarClock size={22} />
                    </div>
                    <h2 className="font-display text-2xl mb-2">Свържете вашия календар</h2>
                    <p className="text-ink-muted text-sm mb-6 max-w-lg">
                        След като одобрите достъпа, всяка нова резервация ще създава събитие директно в календара ви,
                        а отмяната ще го премахва моментално.
                    </p>
                    <button
                        onClick={connect}
                        data-testid="calendar-connect-btn"
                        className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-champagne px-8 py-4 rounded-full text-sm font-medium shadow-gold transition-all"
                    >
                        <Link2 size={16} /> Connect Google Calendar
                    </button>
                    <p className="text-xs text-ink-subtle mt-4">
                        При първо свързване Google ще покаже предупреждение „Unverified app" — кликнете
                        <em> „Advanced → Go to Konstantin Mobile (unsafe)"</em>. Това е нормално в режим на тестване.
                    </p>
                </div>
            )}

            {connected && (
                <div className="p-8 rounded-3xl bg-surface border border-line">
                    <div className="w-14 h-14 rounded-full bg-green-100 border border-green-200 text-green-700 flex items-center justify-center mb-4">
                        <CheckCircle2 size={22} />
                    </div>
                    <h2 className="font-display text-2xl mb-2">Свързан ✓</h2>
                    <div className="text-sm text-ink-muted space-y-1 mb-6">
                        <div><span className="text-ink-subtle">Акаунт:</span> <span className="text-ink">{status.email || "—"}</span></div>
                        {status.connected_at && (
                            <div><span className="text-ink-subtle">Свързан на:</span> <span className="text-ink">{new Date(status.connected_at).toLocaleString("bg-BG")}</span></div>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <a
                            href="https://calendar.google.com"
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm border border-line hover:border-gold/40 text-ink transition-all"
                        >
                            <ExternalLink size={14} /> Отвори Google Calendar
                        </a>
                        <button
                            onClick={disconnect}
                            data-testid="calendar-disconnect-btn"
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm border border-red-200 text-red-700 hover:bg-red-50 transition-all"
                        >
                            <Unlink size={14} /> Прекъсни връзката
                        </button>
                    </div>
                </div>
            )}

            <div className="p-5 rounded-2xl bg-bg border border-line text-sm text-ink-muted space-y-2">
                <div className="font-medium text-ink">Как работи</div>
                <ul className="list-disc list-inside space-y-1">
                    <li>Нова резервация → събитие се създава в календара за &lt;1 секунда</li>
                    <li>Клиент отмени резервацията → събитието изчезва моментално</li>
                    <li>Изпращат се email напомняне 24ч преди и popup напомняне 1ч преди</li>
                    <li>Работи паралелно с ICS feed-a (той остава активен като backup)</li>
                </ul>
            </div>
        </div>
    );
}
